// /app/frontend/src/components/frugal-v2/components/reports/generateClosingPDF.js
// PDF generation for a single closed month (snapshot from frugal_closings)

const fmt = (n) => `$${(typeof n === 'number' ? n : 0).toFixed(2)}`;

export async function generateClosingPDF(closing, t, lang) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 18;
  const summary = closing?.summary || {};
  const isPartial = !summary.schema_version || summary.schema_version < 2;

  // Title
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text('ZenSaver - ' + (t('frugal.reportsHistory.title') || 'Closed Report'), pageWidth / 2, yPos, { align: 'center' });
  yPos += 9;

  // Subtitle (Month name)
  doc.setFontSize(13);
  doc.setTextColor(100, 100, 100);
  doc.text(closing?.month_display || closing?.month_key || '', pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;

  if (isPartial) {
    doc.setFontSize(9);
    doc.setTextColor(180, 100, 0);
    doc.text(t('frugal.reportsHistory.partialReport'), pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
  }

  // Totals row
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  doc.text(`${t('frugal.reportsHistory.budget')}: ${fmt(summary.total_budget)}`, 20, yPos);
  doc.text(`${t('frugal.reportsHistory.spent')}: ${fmt(summary.total_spent)}`, 110, yPos);
  yPos += 7;
  const saved = (summary.total_budget || 0) - (summary.total_spent || 0);
  doc.setTextColor(saved >= 0 ? 16 : 220, saved >= 0 ? 185 : 38, saved >= 0 ? 129 : 38);
  doc.text(`${t('frugal.reportsHistory.saved')}: ${fmt(saved)}`, 20, yPos);
  doc.setTextColor(40, 40, 40);
  doc.text(`${t('frugal.reportsHistory.transactions')}: ${summary.transactions_count || 0}`, 110, yPos);
  yPos += 10;

  yPos = renderInlineTable(
    doc, autoTable, yPos, t('frugal.reportsHistory.income'), summary.income,
    [['budget', 'incomeReceived', 'incomePending']], t
  );
  yPos = renderInlineTable(
    doc, autoTable, yPos, t('frugal.reportsHistory.savings'), summary.savings,
    [['budget', 'savingsExecuted', 'incomePending']], t,
    { totalKey: 'total', extraKeys: ['saved', 'pending'] }
  );

  // Banks & Cards & Receivables & Payables
  if (summary.accounts && (
      summary.accounts.banks?.length > 0 ||
      summary.accounts.cards?.length > 0 ||
      summary.accounts.receivables?.length > 0 ||
      summary.accounts.payables?.length > 0
  )) {
    yPos = sectionTitle(doc, yPos, t('frugal.reportsHistory.banksCards'));
    const rows = [
      ...(summary.accounts.banks || []).map((b) => [b.name, t('frugal.reportsHistory.bank'), fmt(b.balance)]),
      ...(summary.accounts.cards || []).map((c) => [c.name, t('frugal.reportsHistory.card'), fmt(c.balance)]),
      ...(summary.accounts.receivables || []).map((r) => [r.name, 'Receivable', fmt(r.balance)]),
      ...(summary.accounts.payables || []).map((p) => [p.name, 'Payable', fmt(-p.balance)]),
      [t('frugal.reportsHistory.netPosition'), '', fmt(summary.accounts.net_position)],
      ['Total Balance', '', fmt(summary.accounts.total_balance ?? summary.accounts.net_position)],
    ];
    autoTable(doc, {
      startY: yPos,
      head: [[t('frugal.reportsHistory.bank') + '/' + t('frugal.reportsHistory.card'), '', t('frugal.reportsHistory.balance')]],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 20, right: 20 },
      styles: { fontSize: 9 }
    });
    yPos = doc.lastAutoTable.finalY + 8;
  }

  // Vital reserve
  if (summary.vital && summary.vital.total_budget > 0) {
    yPos = sectionTitle(doc, yPos, t('frugal.reportsHistory.vitalReserve'));
    doc.setFontSize(10);
    doc.text(
      `${t('frugal.reportsHistory.budget')}: ${fmt(summary.vital.total_budget)}   ` +
      `${t('frugal.reportsHistory.spent')}: ${fmt(summary.vital.total_spent)}   ` +
      `${t('frugal.reportsHistory.vitalRemaining')}: ${fmt(summary.vital.remaining)}`,
      20, yPos
    );
    yPos += 9;
  }

  // Comparison
  if (summary.previous_month_comparison?.totals) {
    const tot = summary.previous_month_comparison.totals;
    yPos = sectionTitle(doc, yPos, t('frugal.reportsHistory.comparison'));
    doc.setFontSize(10);
    doc.text(
      `${t('frugal.reportsHistory.current')}: ${fmt(tot.current)}   ` +
      `${t('frugal.reportsHistory.previous')}: ${fmt(tot.previous)}   ` +
      `${t('frugal.reportsHistory.diff')}: ${fmt(tot.diff)}`,
      20, yPos
    );
    yPos += 9;
  }

  // Categories table (always last)
  const cats = Array.isArray(summary.categories) ? summary.categories : [];
  if (cats.length > 0) {
    if (yPos > 240) { doc.addPage(); yPos = 18; }
    yPos = sectionTitle(doc, yPos, t('frugal.reportsHistory.categories'));
    autoTable(doc, {
      startY: yPos,
      head: [[
        t('frugal.reportsHistory.category'),
        t('frugal.reportsHistory.budget'),
        t('frugal.reportsHistory.spent'),
        t('frugal.reportsHistory.saved'),
        '%'
      ]],
      body: cats.map((c) => [
        c.category, fmt(c.budgeted), fmt(c.spent), fmt(c.difference),
        `${(c.percent || 0).toFixed(1)}%`
      ]),
      theme: 'striped',
      headStyles: { fillColor: [124, 58, 237] },
      margin: { left: 20, right: 20 },
      styles: { fontSize: 9 }
    });
  }

  doc.save(`ZenSaver-${closing?.month_key || 'report'}.pdf`);
}

function sectionTitle(doc, yPos, title) {
  if (yPos > 270) { doc.addPage(); yPos = 18; }
  doc.setFontSize(13);
  doc.setTextColor(16, 185, 129);
  doc.text(title, 20, yPos);
  doc.setTextColor(40, 40, 40);
  return yPos + 7;
}

function renderInlineTable(doc, autoTable, yPos, title, block, _cols, t) {
  if (!block) return yPos;
  yPos = sectionTitle(doc, yPos, title);
  doc.setFontSize(10);
  doc.text(
    `${t('frugal.reportsHistory.budget')}: ${fmt(block.total)}   ` +
    `${t('frugal.reportsHistory.incomeReceived')}: ${fmt(block.received ?? block.saved)}   ` +
    `${t('frugal.reportsHistory.incomePending')}: ${fmt(block.pending)}`,
    20, yPos
  );
  return yPos + 9;
}
